import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useProjectsStore } from '@/stores/projects'
import { usePullRequestsStore } from '@/stores/pull-requests'
import { useRepositoriesStore } from '@/stores/repositories'
import { useAppEventsStore } from '@/stores/app-events'
import { api } from '@/core/api'
import { toast } from '@/composables/useToast'

export function useAuth() {
  const authStore = useAuthStore()
  const router = useRouter()

  const user = computed(() => authStore.user)
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const loading = computed(() => authStore.loading)

  /** Store JWT token and fetch user profile, then navigate to /projects */
  async function login(token: string) {
    authStore.setToken(token)
    await authStore.fetchProfile()
    router.push('/projects')
  }

  /** Clear tokens, reset all stores, and navigate to landing */
  async function logout() {
    await authStore.logout()

    // Reset all stores
    const projectsStore = useProjectsStore()
    const prStore = usePullRequestsStore()
    const reposStore = useRepositoriesStore()
    projectsStore.$reset()
    prStore.$reset()
    reposStore.$reset()

    router.push('/')
  }

  /** Re-fetch the current user's profile from the backend */
  async function fetchProfile() {
    return authStore.fetchProfile()
  }

  /** Quick check — is there a token stored? */
  function checkAuth(): boolean {
    return api.isAuthenticated()
  }

  // ─── Session event listeners ─────────────────────────────────
  // When the api layer emits an UNAUTHORIZED event (e.g. 401 response),
  // automatically log the user out and show a warning toast.
  const appEvents = useAppEventsStore()
  watch(() => appEvents.unauthorizedAt, (val) => {
    if (val > 0) {
      toast.warning('Session expired. Please log in again.')
      logout()
    }
  })

  return {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    fetchProfile,
    checkAuth,
  }
}
